import { useQuery } from '@apollo/client'
import { shuffle } from 'lodash'
import React, { useEffect } from 'react'
import { useTranslation } from 'react-i18next'

import { Carousel } from '@src/components/Carousel'
import { OutletSlide } from '@src/components/OutletSlide'
import { useFulfilmentFilter } from '@src/hooks/useFulfilmentFilter/useFulfilmentFilter'
import { useMarketplace } from '@src/hooks/useMarketplace'
import { getOutletSearchVariables } from '@src/pages/OutletList/getOutletSearchVariables'
import { outletList_outlets } from '@src/pages/OutletList/queries/__generated__/outletList'
import { outletListDocument } from '@src/pages/OutletList/queries/__generated__/outletList.graphql-interface'
import {
  DateifyOutlet,
  parseOutletDates,
} from '@src/utils/fulfilmentTimes/parsers'

import {
  FeaturedOutletsContainer,
  HeroContainer,
  OutletContainer,
  SliderArrowsContainer,
  HeaderLink,
} from './FeaturedOutlets.styles'
import { FeaturedOutletsSkeleton } from './FeaturedOutletsSkeleton'

import { IconButton } from '../Button/IconButton'

type Props = {
  title: string
}

const NUMBER_OF_OUTLETS_TO_SHOW = 7

export const FeaturedOutlets: React.FC<Props> = ({ title }) => {
  const { t } = useTranslation(['navigation'])
  const marketplace = useMarketplace()
  const fulfilmentFilter = useFulfilmentFilter()

  const variables = getOutletSearchVariables({
    fulfilmentFilter: fulfilmentFilter.data,
    marketplaceId: marketplace.id,
    featured: true,
  })

  const { data, loading } = useQuery(
    outletListDocument,
    variables ? { variables } : { skip: true }
  )

  const [shuffledOutlets, setShuffledOutlets] = React.useState<
    DateifyOutlet<outletList_outlets>[]
  >([])

  useEffect(() => {
    if (data?.outlets) {
      setShuffledOutlets(
        shuffle(data?.outlets.map(parseOutletDates)).slice(
          0,
          NUMBER_OF_OUTLETS_TO_SHOW
        )
      )
    }
  }, [data])

  if (loading) return <FeaturedOutletsSkeleton />

  if (!data) return null

  return (
    <>
      {shuffledOutlets.length > 0 && (
        <OutletContainer>
          <HeroContainer>
            <HeaderLink to="/featured">
              <h2>{title}</h2>
            </HeaderLink>
            <SliderArrowsContainer>
              <IconButton
                className="swiper-swipe-left"
                size={32}
                icon={'caretLeft'}
              />
              <IconButton
                className="swiper-swipe-right"
                size={32}
                icon={'caretRight'}
                alt={t('right')}
              />
            </SliderArrowsContainer>
          </HeroContainer>
          <FeaturedOutletsContainer>
            <Carousel
              slides={shuffledOutlets.map((featuredOutlet, index) => (
                <OutletSlide
                  key={featuredOutlet.id}
                  outlet={featuredOutlet}
                  dataTestId={`featured-outlet-${index}`}
                />
              ))}
            />
          </FeaturedOutletsContainer>
        </OutletContainer>
      )}
    </>
  )
}
